// File: ConfigTemplateMessageLogsPage.tsx

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Tag, Tooltip, Space, Tabs } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import Loader from "../../../../core/common/loader";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { notify } from "../../../../services/toast.config";
import api from "../../../../services/api";
import Datatable from "../../../../core/common/dataTable";

// Types
interface MessageLog {
  id: string;
  provider_config_id: number;
  template_id: number;
  external_message_id: string | null;
  to_number: string;
  message_content: string;
  delivery_status: "sent" | "delivered" | "failed" | "pending";
  delivered_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface PaginationData {
  total_records: number;
  total_pages: number;
  current_page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

// Helpers
const getDeliveryStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "delivered":
      return "success";
    case "sent":
      return "processing";
    case "failed":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

const getDeliveryStatusIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case "delivered":
      return "ti-circle-check";
    case "sent":
      return "ti-send";
    case "failed":
      return "ti-alert-circle";
    case "pending":
      return "ti-clock";
    default:
      return "ti-circle";
  }
};

// Component
const ConfigTemplateMessageLogsPage: React.FC = () => {
  // Route & navigation
  const { configId, templateId } = useParams<{
    configId: string;
    templateId: string;
  }>();

  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const companyId = state?.companyId;
  const companyType = state?.companyType;
  const templateName = state?.templateName || "Template";
  const backRoute =
    state?.backRoute ||
    all_routes.whatsappTemplates.replace(":configId", configId || "");
  const dashboardRoute = state.dashboardRoute || all_routes.adminDashboard;
  const title = state?.title || "Templates";
  const breadcrumb = state?.breadcrumb || "Templates";
  const supportsTemplateSync = state?.supportsTemplateSync || false;

  // State: loading & error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State: data - Store ALL fetched logs
  const [allMessageLogs, setAllMessageLogs] = useState<MessageLog[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    total_records: 0,
    total_pages: 0,
    current_page: 1,
    per_page: 20,
    has_next: false,
    has_prev: false,
  });

  // State: filters (client-side only)
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Filter logs client-side based on status
  const filteredLogs = useMemo(() => {
    if (statusFilter === "all") {
      return allMessageLogs;
    }
    return allMessageLogs.filter((log) => log.delivery_status === statusFilter);
  }, [allMessageLogs, statusFilter]);

  // Calculate summary stats from ALL logs
  const summaryStats = useMemo(() => {
    return {
      total: allMessageLogs.length,
      sent: allMessageLogs.filter((log) => log.delivery_status === "sent")
        .length,
      delivered: allMessageLogs.filter(
        (log) => log.delivery_status === "delivered"
      ).length,
      failed: allMessageLogs.filter((log) => log.delivery_status === "failed")
        .length,
      pending: allMessageLogs.filter((log) => log.delivery_status === "pending")
        .length,
    };
  }, [allMessageLogs]);

  // Columns
  const columns: ColumnsType<MessageLog> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (text) => <span className="text-muted">#{text}</span>,
    },
    {
      title: "Phone Number",
      dataIndex: "to_number",
      key: "to_number",
      width: 150,
      render: (text) => (
        <span className="fw-medium">
          <i className="ti ti-phone me-1 text-muted" />
          {text}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "delivery_status",
      key: "delivery_status",
      width: 120,
      render: (status) => (
        <Tag
          color={getDeliveryStatusColor(status)}
          icon={<i className={`ti ${getDeliveryStatusIcon(status)} me-1`} />}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Message ID",
      dataIndex: "external_message_id",
      key: "external_message_id",
      width: 150,
      render: (text) => (
        <Tooltip title={text || "N/A"}>
          <span className="text-muted font-monospace">
            {text ? `${text.substring(0, 20)}...` : "N/A"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Sent At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date) => (
        <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
          <div className="d-flex flex-column">
            <span className="fw-medium">
              {moment(date).format("MMM DD, YYYY")}
            </span>
            <span className="text-muted small">
              {moment(date).format("hh:mm A")}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Error",
      dataIndex: "error_message",
      key: "error",
      width: 400,
      render: (error) => {
        if (!error) {
          return <span className="text-success">✓ No Error</span>;
        }

        return (
          <Tooltip title={error}>
            <div
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                wordBreak: "break-word",
                maxWidth: "400px",
                lineHeight: "1.4",
              }}
              className="text-danger small">
              <i className="ti ti-alert-circle me-1" />
              {error}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  // Data loader - Fetch ALL logs once (without status filter)
  const loadMessageLogs = useCallback(async () => {
    if (!templateId || !companyId || !companyType) {
      setError("Missing required parameters");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const signature = localStorage.getItem("signature");

      const payload: any = {
        signature,
        company_id: Number(companyId),
        company_type: companyType,
        template_id: Number(templateId),
        page: 1,
        limit: 100,
      };

      const response = await api.post(
        "whatsappService/get-message-logs",
        payload
      );

      const result = response.data?.message?.[0];

      if (result?.status) {
        setAllMessageLogs(result.data || []);
        setPagination(result.pagination || {});
      } else {
        throw new Error(result?.msg || "Failed to load message logs");
      }
    } catch (e: any) {
      const errMsg =
        e?.response?.data?.message?.[0]?.msg ||
        e?.message ||
        "Failed to load message logs";
      setError(errMsg);
      notify.error(errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [templateId, companyId, companyType]);

  // Navigate back with context
  const handleBackToTemplates = () => {
    navigate(backRoute, {
      state: {
        companyId,
        companyType,
        title,
        breadcrumb,
        backRoute: state?.configBackRoute || all_routes.whatsappConfigurations,
        dashboardRoute,
        supportsTemplateSync,
      },
    });
  };

  // Handle status filter change (client-side only, no API call)
  const handleStatusChange = (key: string) => {
    setStatusFilter(key);
  };

  // Effects - Load data only once on mount
  useEffect(() => {
    loadMessageLogs();
  }, []); // Empty dependency array - only run once

  // Error view
  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Message Logs</h4>
            <p>{error}</p>
            <hr />
            <div className="d-flex gap-2">
              <button
                onClick={handleBackToTemplates}
                className="btn btn-primary">
                <i className="ti ti-arrow-left me-2" />
                Back to Templates
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-secondary">
                <i className="ti ti-reload me-2" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Status tabs
  const statusTabs = [
    {
      key: "all",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-list-check me-2" />
          <span className="fw-medium">All</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.total}
          </span>
        </span>
      ),
    },
    {
      key: "delivered",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-circle-check me-2 text-success" />
          <span className="fw-medium">Delivered</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.delivered}
          </span>
        </span>
      ),
    },
    {
      key: "sent",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-send me-2 text-info" />
          <span className="fw-medium">Sent</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.sent}
          </span>
        </span>
      ),
    },
    {
      key: "failed",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-alert-circle me-2 text-danger" />
          <span className="fw-medium">Failed</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.failed}
          </span>
        </span>
      ),
    },
    {
      key: "pending",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-clock me-2 text-warning" />
          <span className="fw-medium">Pending</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.pending}
          </span>
        </span>
      ),
    },
  ];

  // Main render
  return (
    <>
      {isLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Message Logs</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={dashboardRoute}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.whatsappConfigurations}>
                      WhatsApp Config
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <span
                      onClick={handleBackToTemplates}
                      style={{ cursor: "pointer", color: "#007bff" }}>
                      Templates
                    </span>
                  </li>
                  <li className="breadcrumb-item active">Message Logs</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2">
                <button
                  className="btn btn-outline-secondary d-inline-flex align-items-center"
                  onClick={handleBackToTemplates}>
                  <i className="ti ti-arrow-left me-2" />
                  Back to Templates
                </button>
              </div>

              <div className="me-2 mb-2">
                <button
                  className="btn btn-primary d-inline-flex align-items-center"
                  onClick={loadMessageLogs}
                  disabled={isLoading}>
                  <i className="ti ti-refresh me-2" />
                  Refresh
                </button>
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Main Card */}
          <div className="card">
            <div className="card-header border-0 pb-0">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h4 className="mb-1 d-flex align-items-center">
                    <i className="ti ti-message-circle text-primary fs-24 me-2" />
                    Message Logs for "{templateName}"
                  </h4>
                  <p className="text-muted mb-0 small">
                    Tracking delivery status and history for all messages sent
                    using this template
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge badge-lg bg-gradient-primary text-white">
                    <i className="ti ti-database me-1" />
                    {summaryStats.total} Total Messages
                  </span>
                </div>
              </div>

              {/* Status Tabs */}
              <Tabs
                activeKey={statusFilter}
                onChange={handleStatusChange}
                type="line"
                size="small"
                className="modern-tabs"
                items={statusTabs}
              />
            </div>

            {/* Table */}
            <div className="card-body p-0 bg-white" style={{ minHeight: 400 }}>
              {filteredLogs.length > 0 ? (
                <Datatable
                  columns={columns}
                  dataSource={filteredLogs}
                  Selection={false}
                />
              ) : (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: 320 }}>
                  <div className="text-center">
                    <div className="mb-3">
                      <i
                        className="ti ti-inbox text-muted"
                        style={{ fontSize: "4rem" }}
                      />
                    </div>
                    <h5 className="mt-3 mb-2 fw-semibold">
                      No Message Logs Found
                    </h5>
                    <p className="text-muted mb-0">
                      {statusFilter === "all"
                        ? "No messages have been sent using this template yet."
                        : `No ${statusFilter} messages found for this template.`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p className="mb-0">
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              LGS
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default ConfigTemplateMessageLogsPage;
